import {createMissingSchemaLink} from 'src/internal/revamp/links/createMissingSchemaLink';

import type {
  SchemaConfig,
  TopLevelFieldsToSchemaId,
} from 'src/internal/revamp/config/types';
import type {
  SchemaId,
  SchemaIdToApolloLink,
} from 'src/internal/revamp/graphql/compositionFragments/splitQueryBySchema';
import type {ObservabilityFns} from 'src/internal/shared/observability/types';

function getSchemaId(
  schema: SchemaConfig,
  observabilityFns?: ObservabilityFns,
): SchemaId | undefined {
  const firstQuery = Object.values(schema.topLevelFields.queries)[0];
  if (firstQuery) {
    return firstQuery.schemaId;
  }

  const firstMutation = Object.values(schema.topLevelFields.mutations)[0];
  if (firstMutation) {
    return firstMutation.schemaId;
  }

  observabilityFns?.errors.error('No schema ID found in schema configuration', {
    project: 'sail_core',
    extras: {
      firstQueryName: Object.keys(schema.topLevelFields.queries)[0],
      firstMutationName: Object.keys(schema.topLevelFields.mutations)[0],
    },
  });
  return undefined;
}

export type SchemaLinksConfiguration = {
  schemaIdToApolloLink: SchemaIdToApolloLink;
  topLevelFieldNameToMetadata: TopLevelFieldsToSchemaId;
};

export default function createSchemaLinksConfiguration(
  schemas: ReadonlyArray<SchemaConfig>,
  observabilityFns?: ObservabilityFns,
): SchemaLinksConfiguration {
  const schemaIdToApolloLink: SchemaIdToApolloLink = {
    UNKNOWN_SCHEMA: createMissingSchemaLink(),
  };
  const topLevelFieldNameToMetadata = {
    queries: {},
    mutations: {},
  };

  schemas.forEach((schema, index) => {
    const schemaId: SchemaId =
      getSchemaId(schema, observabilityFns) || `v${index}`;

    schemaIdToApolloLink[schemaId] = schema.apolloLink;
    Object.assign(
      topLevelFieldNameToMetadata.queries,
      schema.topLevelFields.queries,
    );
    Object.assign(
      topLevelFieldNameToMetadata.mutations,
      schema.topLevelFields.mutations,
    );
  });

  return {schemaIdToApolloLink, topLevelFieldNameToMetadata};
}
